import { Injectable } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { StockplaceItem } from './logistic.service';
import { Base64UrlService } from './base64url.service';

@Injectable({
    providedIn: 'root',
})
export class CustomerService {
    /** @datatask 1613 */
    public async getSrCustomer(params: { cust_no: string }): Promise<SrCustomerEditGet> { return (await this.datatask.Post(1613, params))[0]; }
    /** @datatask 1614 */
    public async saveSrCustomer(page: SrCustomerEditGet): Promise<ErrorMessageResponse> { return (await this.datatask.Post(1614, page))[0]; }
    constructor(
        private datatask: DataTaskService,
        private base64: Base64UrlService,
    ) {}

    public async customerEditInit(state: 'init'): Promise<CustomerInitGet>;
    public async customerEditInit(state: string): Promise<unknown> {
        const init: CustomerInitGet = await this.datatask.Post(2449, { state: state });

        if (Array.isArray(init?.autoinvoicemethods)) {
            init.autoinvoicemethods = init.autoinvoicemethods.map((method) => ({ autoinvoicemethod_keyno: '' + method.autoinvoicemethod_keyno, autoinvoicemethod_name: this.base64.urlDecode(method.autoinvoicemethod_name) }));
        }

        if (Array.isArray(init?.invoiceintervals)) {
            init.invoiceintervals = init.invoiceintervals.map((interval) => ({ invoiceinterval_keyno: '' + interval.invoiceinterval_keyno, invoiceinterval_name: this.base64.urlDecode(interval.invoiceinterval_name) }));
        }

        if (Array.isArray(init?.stockplaces)) {
            init.stockplaces = init.stockplaces.map((stockplace) => ({ ...stockplace, stockplace_name: this.base64.urlDecode(stockplace.stockplace_name) }));
        }

        if (Array.isArray(init?.users)) {
            init.users = init.users.map((user) => ({ ...user, user_name: this.base64.urlDecode(user.user_name) }));
        }

        return init;
    }

    /**
     * Retrieves customer edit object for the given params.
     *
     * @param params the params to get customer edit object for.
     * @datatask 67
     * @datataskName sp_ws_p2_customer_get_edit
     * @returns a promise containing object with customer edit fields.
     */
    public async getCustomerEdit(params: CustomerEditParams): Promise<CustomerEditGet> {
        return (await this.datatask.Post(67, params))[0];
    }

    public async saveCustomerEdit(params: CustomerEditGet): Promise<SaveCustomerEditResponse> {
        try {
            const response = await this.datatask.Post(70, params, { suppressErrorMessage: true, returnRawResponse: true });
            console.log(response);
            return response;
        } catch (error) {
            if (!!error && typeof error === 'object' && 'response' in error && !!error.response && typeof error.response === 'object' && 'data' in error.response && !!error.response.data && typeof error.response.data === 'object' && 'cust_no' in error.response.data) {
                return error.response.data as SaveCustomerEditResponse;
            } else {
                throw error;
            }
        }
    }

    /**
     * Deletes the customer with the given cust no.
     *
     * @datatask 745
     * @datataskName sp_ws_p2_customer_delete
     * @param params the cust no of the customer to delete.
     * @returns promise containing either nothing or a validation modal.
     */
    public async deleteCustomer(params: GetCustomersMenuParams) {
        return await this.datatask.Post(745, params);
    }

    /**
     * Retrieves the customers menu page get for the customer with the given cust_no.
     *
     * @datatask 2973
     * @datataskName sp_ws_p2_w_customersmenu_get
     * @param params the cust no of the customer to get.
     * @returns promise containing hte page get.
     */
    public async getCustomersMenu(params: GetCustomersMenuParams): Promise<CustomersMenuGet> {
        return (await this.datatask.Post(2973, params))[0];
    }

    /**
     * Retrieves data for customer group changed to.
     *
     * @datatask 1431
     * @datataskName sp_ws_p2_w_customeredit_ch_custgrp_id
     * @param params the cust group to get data for.
     * @returns promise containing data for the customer group.
     */
    public async getCustomerGroupChangedData(params: { custgrp_id: string }): Promise<CustomerGroupChangedData> {
        return (await this.datatask.Post(1431, params))[0];
    }

    /**
     * Assigns a new acc-cust no for the given customer.
     *
     * @datatask 3267
     * @datataskName sp_ws_p2_customer_assign_new_acc_cust_no
     * @param params
     * @returns promise containing the new acc cust no.
     */
    public async assignNewAccCustNo(params: { acc_cust_no: `${number}`; cust_no: `${number}`; businessco_no: `${number}` }): Promise<{ acc_cust_no: `${number}` }> {
        return (await this.datatask.Post(3267, params))[0];
    }

    /**
     * Retrieves next counter for given id and cust_no
     *
     * @datatask 678
     * @datataskName sp_ws_p2_counter_nextnumber_get
     * @param params for retrieving next counter.
     * @returns promise containing the new counter.
     */
    public async getNextCounter(params: { counter_id: string; cust_no: string }): Promise<{ nextnumber: `${number}` }> {
        return (await this.datatask.Post(678, params))[0];
    }

    /**
     * @datatask 3359
     * @datataskName sp_ws_p2_ehf_groupingmethods_list
     */
    public getEhfGroupingMethods(): Promise<EhfGroupItem[]> {
        return this.datatask.Post(3359);
    }

    /**
     * Retrieves list of customer groups.
     *
     * @datatask 69
     * @datataskName sp_ws_p2_custgrp_list
     * @returns promise containing list of customer groups.
     */
    public getCustomerGroups(params?: GetCustomerGroupsParams): Promise<CustomerGroupItem[]> {
        return this.datatask.Post(69, params);
    }

    /**
     * Retrieves list of customer groups 2.
     *
     * @datatask 3972
     * @returns promise containing list of customer groups 2.
     */
    public getCustomerGroups2(params?: GetCustomerGroupsParams): Promise<CustomerGroup2Item[]> {
        return this.datatask.Post(3972, params);
    }

    /**
     * Retrieves list of discount groups for customers.
     *
     * @datatask 3124
     * @datataskName sp_ws_p2_discgrpcust_list
     * @param params
     * @returns promise containing list of discount groups for customers.
     */
    public getCustomerDiscountGroups(params?: GetCustomerDiscountGroupsParams): Promise<CustomerDiscountGroupItem[]> {
        return this.datatask.Post(3124, params);
    }

    /**
     * @datatask 2903
     * @datataskName tt_custbonusdeal_list
     * @returns
     */
    public getCustomerBonusDeals(): Promise<CustomerBonusDealItem[]> {
        return this.datatask.Post(2903);
    }

    /**
     * Retrieves customer claims for the customer with the given cust no.
     *
     * @datatask 2842
     * @datataskName tt_customer_claims
     * @param params
     * @returns promise containing customer claims.
     */
    public async getCustomerClaims(params: GetCustomerClaimsParams): Promise<CustomerClaimsGet> {
        return (await this.datatask.Post(2842, params))[0];
    }

    /**
     * @datatask 1306
     * @datataskName sp_ws_p2_w_findcustomers_get
     * @returns promise containing the find customer get.
     */
    public async getFindCustomers(): Promise<FindCustomersGet> {
        return (await this.datatask.Post(1306))[0];
    }

    public async brregSearch(searchtext: string): Promise<BrRegItem[]> {
        let parameters = '';

        if (!isNaN(+searchtext) && searchtext.length > 8) {
            parameters = `organisasjonsnummer=${parseInt(searchtext)}`;
        } else {
            parameters = `navn=${searchtext}`;
        }

        const response = await fetch(`https://data.brreg.no/enhetsregisteret/api/enheter?${parameters}`);

        if (response.ok) {
            const data = (await response.json())?._embedded?.enheter ?? [];
            return data.map(this.mapBrRegItem);
        } else {
            console.error(response);
            return [];
        }
    }

    private mapBrRegItem = (item: BrRegItem): BrRegItem => {
        let resultstring = item.navn;

        if (item?.forretningsadresse?.postnummer && item?.forretningsadresse?.poststed) {
            resultstring += `, ${item.forretningsadresse?.postnummer} ${item.forretningsadresse?.poststed}`;
        }

        return { ...item, resultnavn: `${resultstring}, Org.nr: ${item.organisasjonsnummer}` };
    };
}

export interface CustomerBonusDealItem {
    custbonusdeal_keyno: `${number}`;
    custbonusdeal_name: string;
    orderby: `${number}`;
}

export interface CustomerEditParams {
    cust_no: string | number;
}

export interface CustomerEditGet {
    activecustomer: string;
    show_activecustomer: '1' | '0';
    cust_no: `${number}`;
    cust_name: string;
    cust_adr_street: string;
    postplace_id_street: string;
    postplace_name_street: string;
    postplace_name_street_onlyname: string;
    cust_adr_mail: string;
    postplace_id: string;
    postplace_name: string;
    postplace_name_onlyname: string;
    postplace_name_visible: string;
    postplace_name_placeholder: string;
    postplace_name_help: string;
    cust_telephone: string;
    custgrp_id: string;
    custgrp2_keyno: string;
    show_custgrp2_keyno: '1' | '0';
    cust_www: string;
    org_no: string;
    country_id: string;
    is_customer: '1' | '0';
    acc_cust_no: `${number}`;
    is_supplyer: '1' | '0';
    acc_supp_no: `${number}`;
    cust_adr_special: '1' | '0';
    cust_adr_3: string;
    cust_adr_postplace: string;
    cust_address: string;
    cust_order_valuta_id: string;
    cust_invoice_valuta_id: string;
    pricelist_id: string;
    pricelist_id2: string;
    purchase_pricelist_id: string;
    purchase_pricelist_id2: string;
    cust_fax: string;
    cust_paym_id: string;
    cust_email: string;
    cust_name_sort: string;
    tax_keyno: string;
    pickstop: '1' | '0';
    show_pickstop: '1' | '0';
    language_id: string;
    postplace_search_street: string;
    postplace_search_mail: string;
    creditstop: '1' | '0';
    creditstop_amount: string;
    cust_delm_no: string;
    cust_business_id: string;
    cust_delt_no: string;
    default_cust_no: string;
    default_cust_name: string;
    cust_coop_id: string;
    businessco_no: `${number}`;
    is_private: '1' | '0';
    invoicemethod_no: string;
    partial_invoicing: '1' | '0';
    cust_note: string;
    bankaccount: string;
    freight_paid_by: '1' | '0';
    seller_no: string;
    show_setting: '1' | '0';
    deliverytime_from_supplier: string;
    supplier_handling_days: string;
    cust_factoring_no: string;
    portal_user_keyno_purchaser: string;
    einvoice: '1' | '0';
    invoice_cust_no: string;
    invoice_cust_name: string;
    delivery_cust_no: string;
    delivery_cust_name: string;
    supplier_stop_purchase: '1' | '0';
    show_supplier_stop_purchase: '1' | '0';
    gln_number: string;
    show_gln_number: '1' | '0';
    region: string;
    show_region: '1' | '0';
    autoinvoice: '1' | '0';
    show_autoinvoice: '1' | '0';
    user_in_charge: string;
    winner_supplier_id: string;
    default_stockplace_id: string;
    invoiceinterval_keyno: string;
    autoinvoicemethod_keyno: string;
    autoinvoicemethod_stopdate: string;
    common_01: string;
    show_confirm_save_ok_always: '1' | '0';
    carriers_paid_by_customer: '1' | '0';
    custbonusdeal_keyno: string;
    bic: string;
    iban: string;
    brreg_find_customer: string;
    discgrpcust_keyno: string;
    show_discgrpcust: '1' | '0';
    spesial_discgrp: '1' | '0';
    setting_show_is_customer: '1' | '0';
    setting_show_is_supplyer: '1' | '0';
    delivery_customer_search_type: CustType;
    bankCode: string;
    ehf_groupingmethod_no: string;
    set_cust_no: '1' | '0';
    reg_datetime: string;
    mod_datetime: string;
    show_contract_id_on_customer: '1' | '0';
    contract_id: string;
    syncronize_acc_cust_no_with_cust_no: '1' | '0';
    autosave: '1' | '0'; // TODO: add this setting
    webpage_heading: string;
    setting_show_winner_supplier_id: '1' | '0';
    webcode: string;
    show_webcode: '1' | '0';
    show_account_no_purchase: '1' | '0';
    account_no_purchase: string;
    account_name_purchase: string;
    setting_customeredit_show_cust_adr_special: '1' | '0';
    setting_customeredit_show_cust_adr_street: '1' | '0';
    setting_customeredit_show_postplace_search_street: '1' | '0';
    setting_customeredit_show_cust_adr_mail: '1' | '0';
    setting_customeredit_show_postplace_search_mail: '1' | '0';
    setting_customeredit_show_cust_adr_3: '1' | '0';
    setting_customeredit_show_cust_adr_postplacepostplace_id: '1' | '0';
    setting_customeredit_show_country_id: '1' | '0';
    setting_customeredit_show_cust_telephone: '1' | '0';
    setting_customeredit_show_cust_email: '1' | '0';
    setting_customeredit_show_cust_fax: '1' | '0';
    setting_customeredit_show_cust_www: '1' | '0';
    setting_customeredit_show_delivery_cust_no: '1' | '0';
    setting_customeredit_show_delivery_cust_name: '1' | '0';
    setting_customeredit_show_invoice_cust_no: '1' | '0';
    setting_customeredit_show_invoice_cust_name: '1' | '0';
    setting_customeredit_show_reg_datetime: '1' | '0';
    setting_customeredit_show_mod_datetime: '1' | '0';
    setting_customeredit_show_businessco_no: '1' | '0';
    setting_customeredit_show_org_no: '1' | '0';
    setting_customeredit_show_brreg_find_customer: '1' | '0';
    setting_customeredit_show_acc_cust_no: '1' | '0';
    setting_customeredit_show_acc_supp_no: '1' | '0';
    setting_customeredit_show_portal_user_keyno_purchaser: '1' | '0';
    setting_customeredit_show_deliverytime_from_supplier: '1' | '0';
    setting_customeredit_show_supplier_handling_days: '1' | '0';
    setting_customeredit_show_language_id: '1' | '0';
    setting_customeredit_show_cust_name_sort: '1' | '0';
    setting_customeredit_show_creditstop: '1' | '0';
    setting_customeredit_show_creditstop_amount: '1' | '0';
    setting_customeredit_show_default_cust_name: '1' | '0';
    setting_customeredit_show_is_private: '1' | '0';
    setting_customeredit_show_common_01: '1' | '0';
    setting_customeredit_show_cust_note: '1' | '0';
    setting_customeredit_show_bankaccount: '1' | '0';
    setting_customeredit_show_bic: '1' | '0';
    setting_customeredit_show_iban: '1' | '0';
    setting_customeredit_show_bankCode: '1' | '0';
    setting_customeredit_show_ehf_groupingmethod_no: '1' | '0';
    setting_customeredit_show_autoinvoicemethod_keyno: '1' | '0';
    setting_customeredit_show_autoinvoicemethod_stopdate: '1' | '0';
    setting_customeredit_show_default_stockplace_id: '1' | '0';
    setting_customeredit_show_user_in_charge: '1' | '0';
    setting_customeredit_show_cust_delm_no: '1' | '0';
    setting_customeredit_show_cust_delt_no: '1' | '0';
    setting_customeredit_show_custgrp_id: '1' | '0';
    setting_customeredit_show_custgrp2_keyno: '1' | '0';
    setting_customeredit_show_discgrpcust_keyno: '1' | '0';
    setting_customeredit_show_spesial_discgrp: '1' | '0';
    setting_customeredit_show_seller_no: '1' | '0';
    setting_customeredit_show_cust_business_id: '1' | '0';
    setting_customeredit_show_cust_coop_id: '1' | '0';
    setting_customeredit_show_cust_order_valuta_id: '1' | '0';
    setting_customeredit_show_cust_invoice_valuta_id: '1' | '0';
    setting_customeredit_show_pricelist_id: '1' | '0';
    setting_customeredit_show_pricelist_id2: '1' | '0';
    setting_customeredit_show_purchase_pricelist_id: '1' | '0';
    setting_customeredit_show_purchase_pricelist_id2: '1' | '0';
    setting_customeredit_show_cust_paym_id: '1' | '0';
    setting_customeredit_show_tax_keyno: '1' | '0';
    setting_customeredit_show_invoicemethod_no: '1' | '0';
    setting_customeredit_show_invoiceinterval_keyno: '1' | '0';
    setting_customeredit_show_cust_factoring_no: '1' | '0';
    setting_customeredit_show_partial_invoicing: '1' | '0';
    setting_customeredit_show_freight_paid_by: '1' | '0';
    setting_customeredit_show_carriers_paid_by_customer: '1' | '0';
    setting_customeredit_show_custbonusdeal_keyno: '1' | '0';
}

export interface SaveCustomerEditResponse {
    cust_no: number;
    error: boolean;
    errors: { code: number; column: keyof CustomerEditGet; message: string; message_modal: string }[];
    message: '';
}
export interface BrRegItem {
    hjemmeside: string;
    aktivitet: string[];
    epostadresse?: string;
    erIKonsern: boolean;
    forretningsadresse: BrRegAddress;
    harRegistrertAntallAnsatte: boolean;
    institusjonellSektorkode: { kode: string; beskrivelse: string };
    konkurs: boolean;
    maalform: string;
    mobil?: string;
    naeringskode1: { kode: string; beskrivelse: string };
    navn: string;
    organisasjonsform: { kode: string; beskrivelse: string };
    organisasjonsnummer: string;
    paategninger: unknown[];
    postadresse?: BrRegAddress;
    registreringsdatoEnhetsregisteret: string;
    registreringsdatoMerverdiavgiftsregisteret: string;
    registreringsdatoMerverdiavgiftsregisteretEnhetsregisteret: string;
    registrertIForetaksregisteret: boolean;
    registrertIFrivillighetsregisteret: boolean;
    registrertIMvaregisteret: boolean;
    registrertIPartiregisteret: boolean;
    registrertIStiftelsesregisteret: boolean;
    respons_klasse: string;
    underAvvikling: boolean;
    underTvangsavviklingEllerTvangsopplosning: boolean;
    _links: { self: { hreft: string } };
}

export interface BrRegItem {
    resultnavn: string;
}

export interface BrRegAddress {
    land: string;
    landkode: string;
    postnummer: string;
    poststed: string;
    adresse: string[];
    kommune: string;
    kommunenummer: string;
}

export interface EhfGroupItem {
    ehf_groupingmethod_no: `${number}`;
    ehf_groupingmethod_name: string;
    orderby: `${number}`;
}

export interface CustomerInitGet {
    addContactsAfterNewCustomer: boolean;
    autoinvoicemethods: AutoInvoiceMethodItem[];
    stockplaces: StockplaceItem[];
    users: UserItem[];
    invoiceintervals: InvoiceIntervalItem[];
}

export interface AutoInvoiceMethodItem {
    autoinvoicemethod_keyno: string;
    autoinvoicemethod_name: string;
}

export interface UserItem {
    user_id: string;
    user_name: string;
}

export interface InvoiceIntervalItem {
    invoiceinterval_keyno: string;
    invoiceinterval_name: string;
}

export interface GetCustomerGroupsParams {
    /**
     * @default '0'
     */
    add_all?: '1' | '0';
}

export interface CustomerGroupItem {
    custgrp_id: string;
    custgrp_name: string;
    orderby: `${number}`;
}

export interface CustomerGroup2Item {
    custgrp_keyno: string;
    custgrp_name: string;
    orderby: `${number}`;
}

export interface GetCustomerDiscountGroupsParams {
    /**
     * @default '0'
     */
    add_from_custgrp?: '1' | '0';
}

export interface CustomerDiscountGroupItem {
    discgrpcust_keyno: `${number}`;
    discgrpcust_name: string;
    orderby: `${number}`;
}

export interface CustomerGroupChangedData {
    cust_order_valuta_id: string;
    cust_invoice_valuta_id: string;
    pricelist_id: string;
    pricelist_id2: string;
    purchase_pricelist_id: string;
    purchase_pricelist_id2: string;
    cust_paym_id: string;
    tax_keyno: `${number}`;
    cust_delm_no: `${number}`;
    cust_delt_no: `${number}`;
}

export type CustType = 'C' | 'A' | 'S' | 'O' | 'D';

export interface GetCustomersMenuParams {
    cust_no: string | number;
}

export interface CustomersMenuGet {
    cust_no: string;
    cust_name: string;
    cust_address: string;
    cust_telephone: string;
    nbr_of_persons: string;
    nbr_of_custacts: string;
    nbr_of_deviations: string;
    weblink_economics: string;
    weblink_custweb: string;
    cust_www: string;
    maplink: string;
    show_special_calc1: '1' | '0';
    show_customer_discgrpprod: '1' | '0';
    nbr_of_documents: string;
    nbr_of_pictures: string;
    nbr_of_orders: string;
    show_button_customer_edi: '1' | '0';
    show_button_timeregister: '1' | '0';
    show_button_materialregister: '1' | '0';
    show_button_deviation: '1' | '0';
    show_button_orders: '1' | '0';
    show_button_pictures: '1' | '0';
    show_button_documents: '1' | '0';
    show_button_forms: '1' | '0';
    show_button_activities: '1' | '0';
    show_button_custactplanning: '1' | '0';
    show_button_edit_customer: '1' | '0';
    show_purchprice: '1' | '0';
    nbr_of_purchprice: string;
    show_button_pricelistfactor: '1' | '0';
    nbr_of_projects: string;
    show_projects: '1' | '0';
    nbr_of_invoices: string;
    show_invoices: '1' | '0';
    show_deliverycustomers: '1' | '0';
    show_button_customerclaims: '1' | '0';
    show_button_customerbonus: '1' | '0';
    show_setting: '1' | '0';
    nbr_of_deliverycustomers: string;
    delivery_cust_no: string;
    cust_delm_no: string;
    show_button_map: '1' | '0';
    cust_email: string;
    cust_note: string;
    show_freightrate: '1' | '0';
    seller_no: string;
    show_button_serviceobjects: '1' | '0';
    nbr_of_serviceobjects: string;
    acc_supp_no: string;
    acc_cust_no: string;
    show_accounting_accounts: '1' | '0';
    acc_supp_no_saldo: string;
    acc_cust_no_saldo: string;
    businessco_no: string;
    bg_tab1: string;
    show_discgrpcust: '1' | '0';
    show_button_orderheadcreate: '1' | '0';
    show_freightratesets: '1' | '0';
}

export interface GetCustomerClaimsParams {
    cust_no: string | number;
}

export interface CustomerClaimsGet {
    cust_no: string;
    cust_name: string;
    invoice_cust_no: string;
    invoice_cust_name: string;
    creditstop: '1' | '0';
    creditstop_amount: string | number;
    notDuePrincipalAmount: string | number;
    overduePrincipalAmount: string | number;
    stoppedDuePrincipalAmount: string | number;
    collectionPrincipalAmount: string | number;
    PrincipalAmount: string | number;
}

export interface FindCustomersGet {
    show_button_brreg: '1' | '0';
    show_last_visited_customers: '1' | '0';
    findcustomers_selection: CustType;
    show_setting: '1' | '0';
    show_badgeinfo: '1' | '0';
    cust_name: string;
    custgrp_id: string;
}

export interface FindCustomersParams {
    cust_name: string;
    custgrp_id: string;
    custtype: CustType;
    lastselect: 'LAST' | 'SEARCH';
}

export interface SrCustomerEditGet {
    cust_no: string; cust_name: string; cust_adr_street: string; postplace_id_street: string;
    postplace_name_street: string; org_no: string; bankaccount: string | number;
}
